import type { CostCenter } from '@src/models/interfaces/costCenter.interface';

import { COST_CENTER_UNASSIGNED_FILTER_VALUE } from '../constants';

/**
 * Expands selected cost-center ids so a client includes its active branches.
 * Unassigned sentinel is preserved as-is. Order is stable: selected ids first,
 * then newly added branch ids in tree order.
 *
 * Backend ops also expand via `expandCostCenterIds`; the PWA expands before
 * calling so PDF/query payloads already match the UI selection.
 */
export const expandCostCenterIds = (
  selectedIds: string[],
  costCenters: Array<Pick<CostCenter, 'id' | 'parentId' | 'active'>>,
): string[] => {
  if (!selectedIds.length) return [];

  const active = costCenters.filter(cc => cc.active !== false);
  const childrenByParent = new Map<string, string[]>();
  active.forEach(cc => {
    if (!cc.parentId) return;
    const list = childrenByParent.get(cc.parentId) ?? [];
    list.push(cc.id);
    childrenByParent.set(cc.parentId, list);
  });

  const result: string[] = [];
  const seen = new Set<string>();

  selectedIds.forEach(id => {
    if (seen.has(id)) return;
    seen.add(id);
    result.push(id);
    if (id === COST_CENTER_UNASSIGNED_FILTER_VALUE) return;
    (childrenByParent.get(id) ?? []).forEach(childId => {
      if (seen.has(childId)) return;
      seen.add(childId);
      result.push(childId);
    });
  });

  return result;
};

/** True when every selected id is either unassigned or a client with branches. */
export const selectedClientIncludesBranches = (
  selectedIds: string[],
  costCenters: Array<Pick<CostCenter, 'id' | 'parentId' | 'active'>>,
): boolean => {
  if (!selectedIds.length) return false;
  const active = costCenters.filter(cc => cc.active !== false);
  const hasChildren = new Set(
    active.filter(cc => cc.parentId).map(cc => cc.parentId as string),
  );
  return selectedIds.some(
    id => id !== COST_CENTER_UNASSIGNED_FILTER_VALUE && hasChildren.has(id),
  );
};

export const normalizeCostCenterFilterIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map(item => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean),
    ),
  );
};
